import React, { useState } from 'react';
import Sticky from 'react-stickynode';
import PropTypes from 'prop-types';

/**
 * Higher Order Component which wraps the consumer context on the wrapped component.
 * @param {*} WrappedComponent - whatever the component we wrap with withSticky.
 * For information and documentation on the plugin please see:
 * https://github.com/yahoo/react-stickynode
 */
const withSticky = (WrappedComponent) => {
  const stickyComponent = (props) => {
    /**
     * Define default state of the component
     * Default: -1 (not sticky)
     */
    const [stickyStatus, setStickyStatus] = useState(-1);

    /**
     * Callback for the onStateChange prop
     */
    const stickyOnStateChangeCallback = (status) => {
      /**
       * 0 - The default status, located at the original position.
       * 1 - The released status, located at somewhere on document, but not default one.
       * 2 - The sticky status, located fixed to the top or the bottom of screen.
       */
      setStickyStatus(status.status);
    };

    const {
      stickyEnabled,
      stickyTop,
      stickyBottom,
      stickyInnerZ,
      stickyEnableTransforms,
      stickyActiveClass,
      stickyReleasedClass,
      stickyShouldFreeze,
    } = props;

    if (stickyEnabled) {
      return (
        <Sticky
          enabled={stickyEnabled}
          top={stickyTop || undefined}
          bottomBoundary={stickyBottom || undefined}
          innerZ={stickyInnerZ || undefined}
          enableTransforms={stickyEnableTransforms || undefined}
          activeClass={stickyActiveClass || undefined}
          releasedClass={stickyReleasedClass || undefined}
          onStateChange={stickyOnStateChangeCallback}
          shouldFreeze={stickyShouldFreeze || undefined}
        >
          <WrappedComponent {...props} stickyStatus={stickyStatus} />
        </Sticky>
      );
    }

    return <WrappedComponent {...props} />;
  };

  /**
   * Prop Types
   */
  stickyComponent.propTypes = {
    stickyEnabled: PropTypes.bool,
    stickyTop: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    stickyBottom: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    stickyInnerZ: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    stickyEnableTransforms: PropTypes.bool,
    stickyActiveClass: PropTypes.string,
    stickyReleasedClass: PropTypes.string,
    stickyOnStateChange: PropTypes.func,
    stickyShouldFreeze: PropTypes.func,
  };

  /**
   * Default props
   */
  stickyComponent.defaultProps = {
    stickyEnabled: false,
    stickyTop: 0,
    stickyBottom: 0,
    stickyInnerZ: '',
    stickyEnableTransforms: true,
    stickyActiveClass: null,
    stickyReleasedClass: null,
    stickyOnStateChange: () => {},
    stickyShouldFreeze: () => {},
  };

  return stickyComponent;
};

export default withSticky;
